import { useQuery } from '@tanstack/react-query';
import {
  organizationQueryOptions,
  siteQueryOptions,
  studyQueryOptions,
  subjectQueryOptions,
} from '../queryOptions';

type HierarchySource = {
  subjectKey?: string;
  siteKey?: string;
  studyKey?: string;
  organizationKey?: string;
};

// Prefer the device's own key; fall back to the resolved parent's key for devices
// assigned above subject level (e.g. site- or study- level assignment carries no subject key).
const resolveEntityKeyByHierarchy = (...keys: Array<string | undefined>) => {
  const assignedKey = keys.find((key) => !!key);

  return assignedKey || '';
};

/**
 * Resolves the full entity hierarchy for a device and fetches each level.
 * Devices can be assigned at subject, site, or study level. This hook walks up
 * the hierarchy from whichever level is assigned so callers always receive the
 * resolved subject/site/study/organization regardless of assignment depth.
 */
export function useResolvedDeviceHierarchy(hierarchySource?: HierarchySource) {
  const subjectKey = resolveEntityKeyByHierarchy(hierarchySource?.subjectKey);

  const subject = useQuery(
    subjectQueryOptions({ key: subjectKey }, { enabled: !!subjectKey }),
  );

  const siteKey = resolveEntityKeyByHierarchy(
    hierarchySource?.siteKey,
    subject.data?.siteKey,
  );

  const site = useQuery(
    siteQueryOptions({ key: siteKey }, { enabled: !!siteKey }),
  );

  const studyKey = resolveEntityKeyByHierarchy(
    hierarchySource?.studyKey,
    site.data?.studyKey,
  );

  const study = useQuery(
    studyQueryOptions({ key: studyKey }, { enabled: !!studyKey }),
  );

  const organizationKey = resolveEntityKeyByHierarchy(
    hierarchySource?.organizationKey,
    study.data?.organizationKey,
  );

  const organization = useQuery(
    organizationQueryOptions(
      { key: organizationKey },
      { enabled: !!organizationKey },
    ),
  );

  return {
    subject,
    site,
    study,
    organization,
    subjectKey,
    siteKey,
    studyKey,
    organizationKey,
  };
}
